// Audio cues for scan results (build plan P2-T7).
// Tones are generated with the Web Audio API — no audio files.
// Every public function wraps its body in try/catch: audio failure
// (no AudioContext, autoplay policy, etc.) must never affect scan flow.

type AudioContextCtor = typeof AudioContext;

// A single shared context, created lazily on first use.
let sharedContext: AudioContext | null = null;

function getContext(): AudioContext | null {
  try {
    if (sharedContext) {
      return sharedContext;
    }
    const w = window as Window & {
      AudioContext?: AudioContextCtor;
      webkitAudioContext?: AudioContextCtor;
    };
    const Ctor: AudioContextCtor | undefined = w.AudioContext ?? w.webkitAudioContext;
    if (!Ctor) {
      return null;
    }
    sharedContext = new Ctor();
    return sharedContext;
  } catch {
    return null;
  }
}

interface ToneStep {
  // Frequency in Hz held over this step.
  frequency: number;
  // Step duration in seconds.
  duration: number;
}

// Plays a sequence of tone steps back-to-back on a single oscillator.
function playSequence(steps: ToneStep[], type: OscillatorType): void {
  try {
    const ctx = getContext();
    if (!ctx) {
      return;
    }
    if (ctx.state === "suspended") {
      void ctx.resume();
    }

    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = type;
    oscillator.connect(gain);
    gain.connect(ctx.destination);

    const start = ctx.currentTime;
    let cursor = start;
    for (const step of steps) {
      oscillator.frequency.setValueAtTime(step.frequency, cursor);
      cursor += step.duration;
    }

    // Gentle envelope to avoid clicks.
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.2, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, cursor);

    oscillator.start(start);
    oscillator.stop(cursor);
  } catch {
    // Audio is best-effort only.
  }
}

// Short rising tone (~150ms) — valid scan, direction consistent.
export function playOk(): void {
  playSequence(
    [
      { frequency: 660, duration: 0.07 },
      { frequency: 990, duration: 0.08 }
    ],
    "sine"
  );
}

// Double beep (~300ms) — valid attendee but ALREADY_IN / ALREADY_OUT.
export function playWarning(): void {
  playSequence(
    [
      { frequency: 720, duration: 0.09 },
      { frequency: 0.0001, duration: 0.06 },
      { frequency: 720, duration: 0.09 }
    ],
    "square"
  );
}

// Descending tone (~200ms) — badge not registered.
export function playRejected(): void {
  playSequence(
    [
      { frequency: 440, duration: 0.1 },
      { frequency: 220, duration: 0.1 }
    ],
    "sawtooth"
  );
}
